import {inject} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {catchError, map, of, take} from 'rxjs';
import {CurrentUserContextService} from '../services/current-user-context.service';
import {AuthService} from '../services/auth.service';
import {defaultLandingPathForRoles, normalizeUserRoles} from '../../app.constants';

/**
 * Ensures users who do not have access to dashboard (specifically AGENT_OPERATOR)
 * land on their designated first page (/profile) instead of hitting /forbidden.
 */
export const dashboardRoleRedirectGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const currentUser = inject(CurrentUserContextService);
  const authService = inject(AuthService);

  const resolveRedirect = (profile: {userRoles?: unknown[] | null; profileCompleted?: boolean} | null, profileRoles: unknown[] | null | undefined) => {
    const combinedRoles = [
      ...normalizeUserRoles(profileRoles),
      ...normalizeUserRoles(authService.effectiveRoles()),
    ];
    const target = defaultLandingPathForRoles(combinedRoles);
    if (target !== '/admin/dashboard' && target !== '/dashboard') {
      // Fresh USERs land on onboarding, but a completed profile has finished
      // it — let them use the dashboard instead of bouncing back.
      if (target === '/profile/onboarding' && profile?.profileCompleted === true) {
        return true;
      }
      console.warn(`[dashboardRoleRedirectGuard] Blocked access to "${state?.url ?? '/admin/dashboard'}". Redirecting to "${target}" for roles:`, combinedRoles);
      return router.createUrlTree([target], {queryParams: route.queryParams});
    }
    return true;
  };

  return currentUser.load().pipe(
    take(1),
    map((profile) => resolveRedirect(profile, profile.userRoles)),
    catchError(() => of(resolveRedirect(null, null)))
  );
};
